import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { getProjects, deleteProject } from '../../../services/api'
import type { Project } from '../../../types/database'
import ProjectForm from '../forms/ProjectForm'

export default function DashboardProjects() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)

  useEffect(() => {
    loadProjects()
  }, [])

  const loadProjects = async () => {
    setLoading(true)
    const data = await getProjects()
    setProjects(data)
    setLoading(false)
  }

  const handleCreate = () => {
    setEditingProject(null)
    setIsFormOpen(true)
  }

  const handleEdit = (project: Project) => {
    setEditingProject(project)
    setIsFormOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return

    const success = await deleteProject(id)
    if (success) {
      loadProjects()
    }
  }

  const handleSave = async () => {
    setIsFormOpen(false)
    setEditingProject(null)
    loadProjects()
  }

  const springConfig = { type: "spring", stiffness: 60, damping: 10 }

  if (loading) {
    return <div className="text-text-primary/70">Loading projects...</div>
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-text-primary">Projects</h2>
        <motion.button
          onClick={handleCreate}
          className="px-5 sm:px-6 py-2.5 sm:py-3 neu rounded-xl font-medium text-text-primary neu-hover touch-manipulation w-full sm:w-auto text-sm sm:text-base"
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          transition={springConfig}
        >
          + New Project
        </motion.button>
      </div>

      {projects.length === 0 ? (
        <div className="neu rounded-2xl p-8 sm:p-12 text-center">
          <p className="text-text-primary/70 mb-4 text-sm sm:text-base">No projects yet</p>
          <button
            onClick={handleCreate}
            className="text-accent hover:text-accent-secondary font-medium transition-colors touch-manipulation text-sm sm:text-base py-2"
          >
            Create your first project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {projects.map((project) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="neu rounded-2xl p-4 sm:p-6 neu-hover"
            >
              <div className="mb-3 sm:mb-4">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-text-primary">
                  {project.title}
                </h3>
                <p className="text-text-primary/70 text-xs sm:text-sm line-clamp-2">
                  {project.description}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                {project.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 neu text-xs text-text-primary/70 rounded-lg"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <motion.button
                  onClick={() => handleEdit(project)}
                  className="flex-1 px-3 sm:px-4 py-2.5 sm:py-2 neu rounded-xl text-text-primary hover:text-accent transition-colors text-xs sm:text-sm font-medium neu-hover touch-manipulation"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Edit
                </motion.button>
                <motion.button
                  onClick={() => handleDelete(project.id)}
                  className="px-3 sm:px-4 py-2.5 sm:py-2 neu rounded-xl text-red-400 hover:text-red-500 transition-colors text-xs sm:text-sm font-medium neu-hover touch-manipulation"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Delete
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {isFormOpen && (
          <ProjectForm
            project={editingProject}
            onSave={handleSave}
            onCancel={() => {
              setIsFormOpen(false)
              setEditingProject(null)
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}


